import { FileService } from './../../../services/file.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, switchMap, shareReplay, first, finalize, retry } from 'rxjs/operators';
import { RegepApplicationService } from '../../services/regep-application.service';
import { DestroyableComponent } from 'src/app/common/destroyable.component';
import { RegepProcessActionTypes } from '../../entities/enums/regepProcessActionTypes';
import { MessageType } from 'src/app/constants/app.constants';
import { SubmitService } from 'src/app/services/submit.service';
import { CommonService } from 'src/app/services/common.service';
import { RegepOrderProcess } from '../../entities/regep-order-process';
import { RegepOrderProcessService } from '../../services/regep-order-process.service';
import { RegepProcessStatus } from '../../entities/enums/regepProcessStatus';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { ComponentCanDeactivate } from 'src/app/+shared/guards/pending-changes-guard.servie';
import { FormGroup } from '@angular/forms';
import { EnvironmentService } from '../../../services/environment.service';

const showTechnicalResourceStatuses: RegepProcessStatus[] = [
  RegepProcessStatus.RequestSent,
  RegepProcessStatus.EngineeringRequestUpdated,
  RegepProcessStatus.EngineeringOpenIssue,
  RegepProcessStatus.EngineeringOk,
  RegepProcessStatus.RequestDenied,
  RegepProcessStatus.RequestWithdrawn,
];

const canEditTechnicalResourceStatuses: RegepProcessStatus[] = [
  RegepProcessStatus.RequestSent,
  RegepProcessStatus.EngineeringRequestUpdated,
];

@Component({
  selector: 'app-regep-result',
  templateUrl: './regep-result.component.html',
  styleUrls: ['./regep-result.component.scss'],
})
export class RegepResultComponent extends DestroyableComponent implements OnInit, ComponentCanDeactivate {
  enums = {
    orderProcessStatus: RegepProcessStatus,
  };

  orderProcess$: Observable<RegepOrderProcess>;
  applicationId$: Observable<number>;
  externalApplicationId$: Observable<string>;
  isLoading: boolean;
  showTechnicalResource$: Observable<boolean>;
  canEditTechnicalResource$: Observable<boolean>;
  technicalResourcesForm: FormGroup;
  historyLogForm: FormGroup;
  preventCanDeactivateCheck: boolean = false;

  constructor(
    public router: Router,
    private route: ActivatedRoute,
    private submitService: SubmitService,
    private commonService: CommonService,
    private applicationService: RegepApplicationService,
    private orderProcessService: RegepOrderProcessService,
    private translateService: NetzTranslateService,
    private fileService: FileService,
    private errorService: ErrorService,
    private environmentService: EnvironmentService
  ) {
    super();
  }

  canDeactivate(): boolean | Promise<boolean> {
    return this.preventCanDeactivateCheck || (!this.historyLogForm?.dirty && !this.technicalResourcesForm?.dirty);
  }

  ngOnInit(): void {
    this.isLoading = true;

    this.applicationId$ = this.route.paramMap.pipe(map(params => Number(params.get('id'))));
    this.orderProcess$ = this.orderProcessService.orderProcess$;
    this.showTechnicalResource$ = this.orderProcess$.pipe(map(op => showTechnicalResourceStatuses.includes(op.status)));
    this.canEditTechnicalResource$ = this.orderProcess$.pipe(map(op => canEditTechnicalResourceStatuses.includes(op.status)));

    this.externalApplicationId$ = this.applicationId$.pipe(
      switchMap(applicationId => {
        return this.applicationService.getApplication(applicationId);
      }),
      map(regepApplication => regepApplication.application_ext_id),
      shareReplay(1),
      first()
    );

    this.preventLeak(this.externalApplicationId$).subscribe(() => (this.isLoading = false));
  }

  public executedProcessAction(actionType: RegepProcessActionTypes, applicationId: number) {
    if (actionType == RegepProcessActionTypes.External) {
      this.navigateToSubmittedPage();
    }

    if (actionType == RegepProcessActionTypes.Internal) {
      this.navigateToJurisdictionOverview(applicationId);
    }
  }

  public async exportCsv(): Promise<void> {
    this.applicationId$.subscribe(applicationId => {
      this.isLoading = true;
      this.applicationService
        .downloadExport(applicationId, 'text/csv', this.translateService.getCurrentLang())
        .pipe(
          finalize(() => (this.isLoading = false)),
          retry(1)
        )
        .subscribe(
          (res: any) => {
            this.fileService.downloadDocument(res.blob, res.fileName, true);
          },
          _ => this.errorService.showError('Export failed.')
        );
    });
  }

  private navigateToSubmittedPage() {
    this.preventCanDeactivateCheck = true;
    this.submitService.setMessage(MessageType.REQUEST_SUBMISSION);
    this.router.navigate(['/submitted']);
  }

  private navigateToJurisdictionOverview(applicationId: number) {
    this.preventCanDeactivateCheck = true;
    this.commonService.showHighlightedRow = applicationId;
    this.router.navigate(['/jurisdiction-overview']);
  }
}
